import { useEffect, useState, type FormEvent } from 'react';
import type { SupplierInput, SupplierListItem } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatDate, formatMoney } from '../format';
import { DataTable, NewButton, PageHeader, Pager, SearchPanel, useUrlFilters, type Column } from '../components/ListPage';

// Təchizatçılar (KT-108, maket 26 siyahı şablonu): axtarış (ad, VÖEN, telefon), «borcumuz var» süzgəci, səhifələmə; kiçik varlıq —
// yaratma və redaktə pəncərədə. Silmə yoxdur. Borc jurnaldan (counterparty_balance).
const PAGE = 25;
const KEYS = ['q', 'debt', 'page'] as const;
const errText = (e: unknown) => (e instanceof ApiError ? e.message : 'Xəta baş verdi');

export function Suppliers({ canEdit }: { canEdit: boolean }) {
  const [f, setF] = useUrlFilters(KEYS);
  const [q, setQ] = useState(f.q);
  const [debt, setDebt] = useState(f.debt === '1');
  const [items, setItems] = useState<SupplierListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string | null; input: SupplierInput } | null>(null);
  const [seq, setSeq] = useState(0);
  const page = Number(f.page) || 1;

  useEffect(() => {
    const t = window.setTimeout(() => {
      if (q.trim() !== f.q) setF({ q: q.trim(), page: '' });
    }, 250);
    return () => window.clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .suppliers({ q: f.q || undefined, debt: f.debt === '1' ? '1' : undefined, page, limit: PAGE })
      .then((r) => {
        if (cancelled) return;
        setItems(r.items);
        setTotal(r.total);
      })
      .catch((e: unknown) => !cancelled && setError(errText(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [f.q, f.debt, page, seq]);

  const debtSum = items.reduce((s, i) => s + Math.round(i.debt * 100), 0) / 100; // qəpiklə cəm (göstəriş)
  const columns: Column<SupplierListItem>[] = [
    { key: 'name', label: 'Ad', render: (s) => s.name },
    { key: 'voen', label: 'VÖEN', mono: true, minor: true, render: (s) => s.voen ?? '—' },
    { key: 'phone', label: 'Telefon', minor: true, render: (s) => s.phone ?? '—' },
    { key: 'last', label: 'Son alış', minor: true, render: (s) => (s.lastPurchaseAt ? formatDate(s.lastPurchaseAt) : '—') },
    { key: 'debt', label: 'Borcumuz', align: 'right', mono: true, render: (s) => (s.debt > 0 ? <span className="bad">{formatMoney(s.debt)}</span> : formatMoney(0)) },
    { key: 'st', label: 'Status', render: (s) => <span className={`badge ${s.active ? 'badge-ok' : 'badge-off'}`}>{s.active ? 'Aktiv' : 'Deaktiv'}</span> },
  ];

  return (
    <main className="list-page">
      <PageHeader
        title="Təchizatçılar"
        sub={`${total} təchizatçı`}
        actions={canEdit ? <NewButton label="Yeni təchizatçı" onClick={() => setEditing({ id: null, input: { name: '', voen: null, phone: null } })} /> : undefined}
      />
      <SearchPanel
        onSearch={() => setF({ q: q.trim(), debt: debt ? '1' : '', page: '' })}
        onClear={() => {
          setQ('');
          setDebt(false);
          setF({ q: '', debt: '', page: '' });
        }}
      >
        <label className="field">
          <span>Axtar</span>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ad, VÖEN, telefon" />
        </label>
        <label className="inline-check">
          <input type="checkbox" checked={debt} onChange={(e) => setDebt(e.target.checked)} /> Borcumuz var
        </label>
      </SearchPanel>
      {editing && (
        <SupplierForm
          id={editing.id}
          initial={editing.input}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setSeq((n) => n + 1);
          }}
        />
      )}
      <DataTable
        label="Təchizatçılar"
        loading={loading}
        error={error}
        rows={items}
        rowKey={(s) => s.id}
        columns={columns}
        onEdit={canEdit ? (s) => setEditing({ id: s.id, input: { name: s.name, voen: s.voen, phone: s.phone } }) : undefined}
        editLabel="Təchizatçını redaktə et"
        empty={<p className="muted">{f.q || f.debt ? 'Süzgəcə uyğun təchizatçı yoxdur.' : 'Təchizatçı yoxdur. Alış sənədində və ya «Yeni təchizatçı» ilə əlavə olunur.'}</p>}
        footer={items.length > 0 ? { name: 'Bu səhifədə', debt: formatMoney(debtSum) } : undefined}
      />
      <Pager page={page} limit={PAGE} total={total} unit="təchizatçı" onPage={(p) => setF({ page: String(p) })} />
    </main>
  );
}

function SupplierForm({ id, initial, onCancel, onSaved }: { id: string | null; initial: SupplierInput; onCancel: () => void; onSaved: () => void }) {
  const [f, setF] = useState<SupplierInput>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (id) await api.supplierUpdate(id, f);
      else await api.supplierCreate(f);
      onSaved();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-overlay" role="presentation">
      <form className="modal-box contract-form" role="dialog" aria-modal="true" aria-label={id ? 'Təchizatçını redaktə et' : 'Yeni təchizatçı'} onSubmit={(e) => void submit(e)}>
        <h2>{id ? 'Təchizatçı' : 'Yeni təchizatçı'}</h2>
        <label className="field">
          <span>Ad</span>
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required maxLength={200} autoFocus />
        </label>
        <label className="field">
          <span>VÖEN</span>
          <input value={f.voen ?? ''} onChange={(e) => setF({ ...f, voen: e.target.value.replace(/\D/g, '').slice(0, 10) || null })} inputMode="numeric" />
        </label>
        <label className="field">
          <span>Telefon</span>
          <input value={f.phone ?? ''} onChange={(e) => setF({ ...f, phone: e.target.value || null })} inputMode="tel" />
        </label>
        {error && (
          <p className="bad" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={busy || !f.name.trim()}>
            Yadda saxla
          </button>
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Ləğv et
          </button>
        </div>
      </form>
    </div>
  );
}
